import React from 'react';
import { Link } from 'react-router-dom';
import SEO from "@/components/SEO";
import { SEO_KEYWORDS } from "@/seo/seoData";
import { motion } from 'framer-motion';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, Globe, Lightbulb, CheckCircle } from 'lucide-react';

const HERO_IMAGE = "service.webp";

const solutions = [
  {
    title: 'Digital Transformation',
    description: 'End-to-end digital transformation services to modernize your business operations and customer experiences.',
    features: ['Business Process Automation', 'Legacy System Modernization', 'Cloud Migration', 'Digital Strategy Consulting']
  },
  {
    title: 'Growth Marketing',
    description: 'Data-driven marketing strategies designed to accelerate your business growth and maximize ROI.',
    features: ['SEO & Content Marketing', 'Paid Advertising', 'Conversion Optimization', 'Marketing Analytics']
  },
  {
    title: 'Web & App Development',
    description: 'Custom web and mobile application development tailored to your unique business needs.',
    features: ['Custom Web Applications', 'E-Commerce Platforms', 'Mobile App Development', 'API Integrations']
  },
  {
    title: 'Brand & Design',
    description: 'Strategic branding and design services that help you stand out and connect with your audience.',
    features: ['Brand Identity', 'UI/UX Design', 'Graphic Design', 'Design Systems']
  }
];

export default function SolutionsPage() {
  return (
    <Layout>
   <SEO
  title="Get Complete Digital solutions for you business | Square Trigger"
  description="Find Comprehensive Digital solutions to transform your business in today's fast-moving market. Request a Free Quote."
  keywords={SEO_KEYWORDS.solutions}
  ogTitle="Get Complete Digital solutions for you business | Square Trigger"
  ogDescription="Find Comprehensive Digital solutions to transform your business in today's fast-moving market. Request a Free Quote."
  ogSiteName="Square Trigger Services"
  ogImage="https://squaretrigger.com/service.webp"
/>
      {/* Hero Section */}
<section className="relative overflow-hidden">
  {/* Background Image */}
  <div className="absolute inset-0">
    <div
      className="absolute inset-0 bg-cover bg-center"
      style={{ backgroundImage: `url(${HERO_IMAGE})` }}
    />
  </div>

  <div className="container-custom relative z-10 px-5 pt-[20px] pb-[44px] sm:px-5 sm:pt-[90px] sm:pb-[56px] lg:pt-[60px] lg:pb-[88px]">
    <div className="grid items-center gap-14 lg:grid-cols-[1.1fr_0.9fr]">
      {/* Left Content */}
      <div className="text-center lg:text-left">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-5 flex justify-center sm:mb-2 lg:justify-start"
        >
          <Badge className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/20 px-3 py-2 text-primary-foreground sm:px-4">
            <Globe className="h-4 w-4" />
            <span className="text-sm font-medium">Our Solutions</span>
          </Badge>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className='mx-auto m-0 mb-[18px] max-w-[820px] font-["Lato",system-ui,sans-serif] text-[clamp(2.2rem,4vw,3.25rem)] font-extrabold leading-[1.15] text-background lg:mx-0'
        >
          Comprehensive{" "}
          <span className="gradient-text">Digital Solutions</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mx-auto m-0 mb-6 max-w-[700px] text-[1.08rem] font-medium leading-[1.7] text-white/85 backdrop-blur-[2px] drop-shadow-[0_2px_8px_rgba(0,0,0,0.45)] lg:mx-0"
        >
          Integrated digital services designed to help your business thrive in
          the modern marketplace.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="flex flex-col flex-wrap items-stretch justify-center gap-[14px] sm:flex-row sm:items-center lg:justify-start"
        >
          <Button
            variant="gradient"
            asChild
            className="group inline-flex min-h-[52px] w-full items-center justify-center rounded-[10px] px-7 py-[14px] text-[1rem] font-bold sm:w-auto"
          >
            <Link
              to="/get-started"
              className="flex items-center justify-center gap-2"
            >
              Get Started
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Link>
          </Button>
        </motion.div>
      </div>

     
      <div className="hidden lg:flex items-center justify-center min-h-[420px]">
        <div className="h-[420px] w-full max-w-[520px]" />
      </div>
    </div>
  </div>
</section>
      {/* Solutions Grid */}
      <section className="section-padding bg-background">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-sm font-medium text-primary uppercase tracking-wider">What We Offer</span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mt-3">
              Solutions for Every{' '}
              <span className="gradient-text">Business Need</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {solutions.map((solution, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="p-8 h-full border-border hover:border-primary/30 transition-all duration-300 card-hover flex flex-col items-center text-center md:items-start md:text-left">
                  <div className="w-14 h-14 rounded-xl gradient-primary flex items-center justify-center mb-6 mx-auto md:mx-0">
                    <Lightbulb className="h-7 w-7 text-primary-foreground" />
                  </div>
                  <h3 className="text-2xl font-display font-bold text-foreground mb-4">
                    {solution.title}
                  </h3>
                  <p className="text-muted-foreground mb-6">
                    {solution.description}
                  </p>
                  <ul className="inline-grid md:grid space-y-3 text-left">
                    {solution.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-3 w-fit">
                        <CheckCircle className="h-5 w-5 text-success flex-shrink-0" />
                        <span className="text-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 gradient-primary">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-primary-foreground mb-6">
              Ready to Transform Your Business?
            </h2>
            <p className="text-lg text-primary-foreground/80 mb-8">
              Let's discuss which solution is right for your business and create a customized plan for success.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
             <Button variant="premium" size="lg" asChild className="bg-background text-foreground hover:bg-background/90 min-w-[240px] justify-center">
                <Link to="/get-started">
                  Get Started
                  {/* <ArrowRight className="h-5 w-5 ml-2" /> */}
                </Link>
              </Button>
             <Button variant="glass" size="lg" asChild className="group bg-background text-foreground hover:bg-background/90 min-w-[240px] justify-center">
                <Link to="/contact">
                  Contact Us
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
